// @ts-nocheck
import { useState } from 'react';
import {
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconCalendarEvent,
  IconClock,
  IconMapPin,
  IconRepeat,
} from '@tabler/icons-react';
import { Avatar, AvatarGroup, Badge, Box, Button, Calendar, Card, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { mockEvents, calendarStats, CalendarEvent } from '../mockData/calendar';

const formatTime = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(date);
};

const isSameDay = (date1: Date, date2: Date) => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

const EventItem = ({ event }: { event: CalendarEvent }) => (
  <Box
    p="sm"
    style={{
      backgroundColor: 'var(--mantine-color-gray-light)',
      borderRadius: 8,
      borderLeft: `4px solid var(--mantine-color-${event.color}-filled)`,
    }}
  >
    <Group justify="space-between" mb="xs">
      <Group gap="xs">
        <Badge size="xs" color={event.color}>{event.type}</Badge>
        {event.isRecurring && <IconRepeat size={12} color="var(--mantine-color-dimmed)" />}
      </Group>
      <Text size="xs" c="dimmed">
        {event.allDay ? 'All day' : `${formatTime(event.start)} - ${formatTime(event.end)}`}
      </Text>
    </Group>
    <Text size="sm" fw={500}>{event.title}</Text>
    {event.location && (
      <Group gap={4} mt={4}>
        <IconMapPin size={12} color="var(--mantine-color-dimmed)" />
        <Text size="xs" c="dimmed">{event.location}</Text>
      </Group>
    )}
    {event.attendees && event.attendees.length > 0 && (
      <Group justify="space-between" mt="xs">
        <AvatarGroup>
          {event.attendees.slice(0, 3).map(attendee => (
            <Avatar key={attendee.id} size="xs" radius="xl" color="blue">
              {attendee.name.split(' ').map(n => n[0]).join('')}
            </Avatar>
          ))}
          {event.attendees.length > 3 && (
            <Avatar size="xs" radius="xl" color="gray">+{event.attendees.length - 3}</Avatar>
          )}
        </AvatarGroup>
      </Group>
    )}
  </Box>
);

export default function CalendarPage() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const todayEvents = mockEvents.filter(e => isSameDay(e.start, new Date()));
  const selectedDateEvents = mockEvents.filter(e => isSameDay(e.start, selectedDate));
  const upcomingEvents = mockEvents
    .filter(e => e.start > new Date())
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .slice(0, 5);

  // Get dates that have events for calendar highlighting
  const eventDates = mockEvents.map(e => e.start);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Calendar</Title>
          <Text c="dimmed" size="sm">Manage your schedule and events</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>New Event</Button>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-blue-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconCalendarEvent size={20} color="var(--mantine-color-blue-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Today</Text>
              <Text fw={600}>{calendarStats.todayEvents} events</Text>
            </Box>
          </Group>
        </Card>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-green-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconClock size={20} color="var(--mantine-color-green-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Upcoming</Text>
              <Text fw={600}>{calendarStats.upcomingMeetings} meetings</Text>
            </Box>
          </Group>
        </Card>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-orange-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconCalendarEvent size={20} color="var(--mantine-color-orange-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Pending</Text>
              <Text fw={600}>{calendarStats.pendingInvites} invites</Text>
            </Box>
          </Group>
        </Card>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-violet-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconRepeat size={20} color="var(--mantine-color-violet-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Recurring</Text>
              <Text fw={600}>{calendarStats.recurringMeetings} meetings</Text>
            </Box>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Calendar and Events */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Calendar */}
        <Card>
          <Text fw={600} mb="md">Calendar</Text>
          <Calendar
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            getDayProps={(date) => ({
              style: eventDates.some(ed => isSameDay(ed, date))
                ? { backgroundColor: 'var(--mantine-color-blue-light)' }
                : undefined,
            })}
          />
        </Card>

        {/* Selected Date Events */}
        <Card>
          <Text fw={600} mb="md">{formatDate(selectedDate)}</Text>
          <Stack gap="sm">
            {selectedDateEvents.length === 0 ? (
              <Box ta="center" py="xl">
                <IconCalendarEvent size={48} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed" mt="md">No events on this day</Text>
              </Box>
            ) : (
              selectedDateEvents.map(event => (
                <EventItem key={event.id} event={event} />
              ))
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Upcoming Events */}
      <Card>
        <Text fw={600} mb="md">Upcoming Events</Text>
        <Stack gap="sm">
          {upcomingEvents.map(event => (
            <Group key={event.id} justify="space-between" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
              <Group gap="sm">
                <Box
                  style={{
                    width: 4,
                    height: 40,
                    borderRadius: 4,
                    backgroundColor: `var(--mantine-color-${event.color}-filled)`,
                  }}
                />
                <Box>
                  <Text size="sm" fw={500}>{event.title}</Text>
                  <Text size="xs" c="dimmed">
                    {formatDate(event.start)} at {formatTime(event.start)}
                  </Text>
                </Box>
              </Group>
              <Badge size="sm" color={event.color}>{event.type}</Badge>
            </Group>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}
